import {v4 as uuidv4} from 'uuid';

import makeUseGlobalClientState, {
  UseGlobalClientStateOutput,
} from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';

import {ChatDraftStatus, setDraftState} from '../ChatDraftState';
import type {ChatRequest, NewChatRequest} from './ChatRouter';
import {getDefaultAgentType} from './ChatTypeUtil';

const AI_PANEL_STATE_KEY = 'ai-panel-state-v2';

export interface AIPanelState {
  /**
   * A client-local ID used as the key for the ChatRouter component. This allows us to ensure that
   * state does not leak between chat sessions when rendering ChatRouter. This is simply not the chat
   * ID since that does not exist when a new chat is started.
   */
  clientChatId: string;

  /**
   * The chat session that is currently active.
   */
  activeChat: ChatRequest;

  // Whether the history is shown.
  showHistory: boolean;

  // The user's preference for whether history is shown.
  showHistoryPreference: boolean;

  initialModal: string | undefined;
}

export const panelStateHook = makeUseGlobalClientState<AIPanelState>({
  activeChat: {
    id: null,
    type: null,
  },
  showHistory: false,
  showHistoryPreference: false,
  clientChatId: uuidv4(),
  initialModal: undefined,
});

export function useAISidepanelGlobalClientState(): UseGlobalClientStateOutput<AIPanelState> {
  return panelStateHook.useGlobalClientState(AI_PANEL_STATE_KEY);
}

export function openExistingChat(chatSessionId: string): void {
  panelStateHook.updateState(AI_PANEL_STATE_KEY, {
    clientChatId: uuidv4(),
    activeChat: {id: chatSessionId, type: null},
    // TODO: Can we avoid resetting these values?
    showHistoryPreference: false,
    showHistory: false,
    initialModal: undefined,
  });
}

export function openNewChat(newChatRequest: NewChatRequest, options: {modal?: string} = {}): void {
  // Initialize draft state for the new chat with agent type and publicDataEnabled
  const agentType = getDefaultAgentType(newChatRequest.agentType);
  setDraftState(null, {
    status: ChatDraftStatus.DRAFT,
    message: {
      text: '',
      selectedFiles: [],
    },
    settings: agentType ? {agentType, publicDataEnabled: false} : undefined,
  });

  panelStateHook.updateState(AI_PANEL_STATE_KEY, {
    clientChatId: uuidv4(),
    activeChat: newChatRequest,
    // TODO: Can we avoid resetting these values?
    showHistoryPreference: false,
    showHistory: false,
    initialModal: options.modal,
  });
}
